<!--
ReconciliationResultModal — SHARED result viewer for the two manual
reconciliation doors (#27's follow-up, "macanetas" round).

  POST subscriptions/admin/subscriptions/{id}/sync   (lane='cycle')
  POST ai/credits/admin/sync/{franquia_id}            (lane='credit_purchase')

Both routes answer with the SAME per-charge row shape
(Subscriptions_SubscriptionSyncRow) — that is why there is ONE modal here,
not two. The calling page owns the HTTP call and the button state; this
modal only DISPLAYS a result it is handed via eventBus. It never fetches.

Three traps this modal exists to not repeat (see the task brief for the
full write-up):
  1. A refusal never reaches this modal — the composable that made the call
     already threw on the envelope. Nothing to guard here.
  2. The credits route has no `can_reconcile` field to gate on — and this
     modal doesn't gate anything, it only renders whatever `rows` it is
     handed. Gating happens at the button, in the page.
  3. `truncated` only exists on the subscription-sync response (the credits
     sweep scans local rows, not a remote listing, so it can never be cut
     off) — the warning below is keyed off `payload.truncated === true`,
     never assumed from the presence of the field.
-->

<template>
  <modal
    elem-id="modal_reconciliation_result"
    :title="modalTitle"
    :show="isVisible"
    size="modalMd"
    :close-label="t('Fechar')"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="reconciliation-result-modal">
        <!-- Trap 3: the provider's own listing came back cut off — showing
             the rows we DO have without saying so would read as "fully
             synced" when it might not be. -->
        <box
          v-if="truncated"
          type="warning"
          icon="pi pi-exclamation-triangle"
          :title="t('Lista do provedor veio cortada')"
          elem-id="boxReconciliationTruncated"
        >
          <div data-testid="reconciliation-result-truncated-warning">
            {{ t('O provedor retornou mais cobranças do que o limite de leitura desta varredura. A sincronia pode estar incompleta — rode novamente ou confira diretamente no provedor.') }}
          </div>
        </box>

        <data-table
          v-if="rows.length > 0"
          test-id-prefix="reconciliation-result"
          :config="tableConfig"
          :data="displayRows"
          display-mode="table"
        />

        <inline-message
          v-else
          severity="info"
          data-testid="reconciliation-result-empty"
        >
          {{ t('Nada pendente de conciliação — nenhuma cobrança para revisar.') }}
        </inline-message>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import Modal from '../../../ui/Modal.vue'
import Box from '../../../ui/Box.vue'
import InlineMessage from '../../../ui/InlineMessage.vue'
import DataTable from '../../../grid/DataTable.vue'
import { translateReconciliationAction, translateReconciliationDetail } from '../../../composables/useReconciliationSync'
import type { Subscriptions_SubscriptionSyncRow } from '../../../contracts/Subscriptions_SubscriptionSyncRow'

/**
 * Payload the calling page hands over via
 * `eventBus.emit('modal.reconciliation-result.open', payload)`.
 *
 * `title` is already localized by the caller (it names what was synced —
 * e.g. "Assinatura #123" or the franchise's name — context this modal has
 * no way to know on its own). `truncated` is only ever sent by the
 * subscription-sync door; the credits door has no such field and must not
 * set it.
 */
export interface ReconciliationResultPayload {
  title: string
  rows: Subscriptions_SubscriptionSyncRow[]
  truncated?: boolean
}

const { t } = useI18n()

const EMPTY = '—'

const isVisible = ref(false)
const payload = ref<ReconciliationResultPayload | null>(null)

const modalTitle = computed(() => payload.value?.title ?? t('Resultado da conciliação'))
const rows = computed((): Subscriptions_SubscriptionSyncRow[] => payload.value?.rows ?? [])
const truncated = computed(() => payload.value?.truncated === true)

// ── Vocabulary translation ─────────────────────────────────────────────────
//
// action/detail translation is owned by useReconciliationSync — NOT this
// modal. That composable's own docblock explains why these are NOT t() keys:
// action/detail carry machine codes owned by the backend, so this stays a
// plain lookup, called directly (no extra t() wrap on its already-PT-BR
// output, and never on a raw code we don't recognize).
//
// `lane` has no such shared owner (the composable's vocabulary is
// action/detail only) — kept local.

function actionLabel(action?: string): string {
  if (!action) return EMPTY
  return translateReconciliationAction(action, t)
}

const LANE_LABELS: Record<string, string> = {
  cycle: 'Ciclo de assinatura',
  credit_purchase: 'Compra de créditos',
}

function laneLabel(lane?: string): string {
  if (!lane) return EMPTY
  const key = LANE_LABELS[lane]
  return key ? t(key) : lane
}

function detailLabel(detail?: string | null): string {
  if (!detail) return EMPTY
  return translateReconciliationDetail(detail, t) ?? EMPTY
}

// ── Display rows + DataTable config ──────────────────────────────────────

const displayRows = computed(() =>
  rows.value.map((r, idx) => ({
    _row_key: r.charge_id ? `${r.lane ?? 'row'}-${r.charge_id}` : `${r.lane ?? 'row'}-${idx}`,
    charge_id_display: r.charge_id ?? EMPTY,
    lane_display: laneLabel(r.lane),
    remote_status_display: r.remote_status ?? EMPTY,
    // Raw action kept alongside the label — rowClass reads this to flag a
    // failed row, so it must not be lost behind the translated string.
    action: r.action,
    action_display: actionLabel(r.action),
    detail_display: detailLabel(r.detail),
  })),
)

const tableConfig = computed(() => ({
  tableId: 'tabelaReconciliationResult',
  columns: [
    { data: 'charge_id_display', title: t('Cobrança') },
    { data: 'lane_display', title: t('Via') },
    { data: 'remote_status_display', title: t('Status no provedor') },
    { data: 'action_display', title: t('Ação') },
    { data: 'detail_display', title: t('Detalhe') },
  ],
  pagination: true,
  pageLength: 25,
  dataKey: '_row_key',
  // A failed row is the one thing on this screen that needs attention —
  // it has to jump out, not blend into a table of "already synced" rows.
  rowClass: (row: Record<string, unknown>) =>
    row.action === 'failed' ? 'reconciliation-result-row-failed' : '',
  rowTestId: (row: Record<string, unknown>) =>
    row?._row_key ? `reconciliation-result-row-${row._row_key as string}` : '',
}))

// ── Open / close ──────────────────────────────────────────────────────────

function openModal(newPayload?: ReconciliationResultPayload): void {
  log('ReconciliationResultModal', 'openModal', 'Exibindo resultado de conciliação', {
    rowCount: newPayload?.rows?.length ?? 0,
    truncated: newPayload?.truncated === true,
  })
  if (!newPayload) return
  payload.value = newPayload
  isVisible.value = true
}

function closeModal(): void {
  log('ReconciliationResultModal', 'closeModal', 'Fechando resultado de conciliação')
  isVisible.value = false
  payload.value = null
}

onMounted(() => {
  eventBus.on('modal.reconciliation-result.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.reconciliation-result.open', openModal)
})
</script>

<style scoped>
/* Failed rows get a red tint — the one outcome on this screen that needs
   staff attention has to jump out of a table of "already synced" rows. */
:deep(.reconciliation-result-row-failed > td) {
  background: rgba(220, 38, 38, 0.1);
}
</style>
